"use client"

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react"
import { useLightweightHomes, LightweightHomesResponse } from "../hooks/lightweight-homes.hook"
import { useRouter, usePathname } from "next/navigation"
import { useCurrentSearchParams } from "@/shared/hooks/use-current-search-params"
import { buildUrl } from "@/shared/lib/utils"
import { Input } from "@/shared/components/ui/input"
import { Button } from "@/shared/components/ui/button"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverAnchor,
} from "@/shared/components/ui/popover"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { useSession } from "@/features/auth/context/session.context"
import {
  shouldMaskHomeownerData,
  maskHomeownerLastName,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"

interface HomeOption {
  value: string
  label: string
  fullname?: string
  phone?: string
  address?: string
  userId?: number
}

interface ReusableHomeSearchInputProps {
  onSelectHouse?: (houseId: string | null) => void
  selectedValue?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  showSelectedCard?: boolean
  // For map page - handles URL updates
  updateUrl?: boolean
  // For work orders - form integration
  formMode?: boolean
  fetchAllHomes?: boolean
}

function ReusableHomeSearchInput({
  onSelectHouse,
  selectedValue = "",
  onValueChange,
  placeholder = "Search homes by name, address, or phone number...",
  emptyText = "No homes found.",
  disabled = false,
  className,
  showSelectedCard = true,
  updateUrl = false,
  formMode = false,
  fetchAllHomes = false,
}: ReusableHomeSearchInputProps) {
  // Core state - only what we absolutely need
  const [inputValue, setInputValue] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { regionId, isLoading: regionLoading } = useRegionId()
  const { session } = useSession()
  const shouldFetchHomes = fetchAllHomes || (!regionLoading && regionId !== null && regionId !== undefined)
  const regionForFetch = fetchAllHomes ? undefined : (regionId ?? undefined)
  const { data: homesData, isLoading: isLoadingHomes } = useLightweightHomes(
    regionForFetch,
    { enabled: shouldFetchHomes }
  )
  const router = useRouter()
  const pathname = usePathname()
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  
  // Get current search params (only for map page)
  const currentParams = useCurrentSearchParams([
    "overallStatus",
    "roofing",
    "kindOfRoofWork",
    "medicalNeeds",
    "homeownerAge",
    "isSingleParent",
    "numChildrenUnder18",
    "isFirstResponder",
    "isVeteran",
    "femaStatus",
    "sbaStatus",
    "residenceType",
    "structureType",
    "assessmentCompleted",
    "search",
    "homeId"
  ])

  // Transform homes data into options
  const homeOptions: HomeOption[] = useMemo(() => {
    if (!homesData) return []
    
    const response = homesData as LightweightHomesResponse
    if (!response.success || !response.data) return []

    return response.data.map((home: any) => ({
      value: home.id.toString(),
      label: `${home.homeowner?.user?.fullname || 'Unknown'} - ${home.address}${home.homeowner?.user?.phone ? ` (${home.homeowner.user.phone})` : ''}`,
      fullname: home.homeowner?.user?.fullname,
      phone: home.homeowner?.user?.phone,
      address: home.address,
      userId: home.homeowner?.user?.id,
    }))
  }, [homesData])

  const getDisplayLabel = useCallback(
    (option: HomeOption) => {
      const mask = shouldMaskHomeownerData(session?.role, session?.sub, option.userId)
      if (!mask || !option.fullname) return option.label
      const name = maskHomeownerLastName(option.fullname)
      const addr = maskHomeownerAddress(option.address)
      const ph = option.phone ? ` (${maskHomeownerPhone(option.phone)})` : ''
      return `${name} - ${addr}${ph}`
    },
    [session?.role, session?.sub]
  )

  // Find selected home for display
  const selectedHome = useMemo(() => {
    if (!selectedValue || !homesData?.success) return null
    return homesData.data.find(
      (home: any) => home.id.toString() === selectedValue
    ) as any
  }, [selectedValue, homesData])

  // Filter options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return homeOptions
    
    const searchLower = inputValue.toLowerCase()
    return homeOptions.filter(option =>
      option.label.toLowerCase().includes(searchLower)
    )
  }, [homeOptions, inputValue])

  // Initialize input value from props or URL params
  useEffect(() => {
    if (formMode && selectedValue) {
      // For form mode, use the selectedValue prop
      const selectedOption = homeOptions.find(option => option.value === selectedValue)
      if (selectedOption) {
        setInputValue(getDisplayLabel(selectedOption))
      } else {
        setInputValue(selectedValue)
      }
    } else if (updateUrl) {
      // For map mode, check URL params
      const searchParam = currentParams.search || ""
      const homeIdParam = currentParams.homeId || ""
      
      if (searchParam && !isNaN(Number(searchParam))) {
        const selectedOption = homeOptions.find(option => option.value === searchParam)
        if (selectedOption) {
          setInputValue(getDisplayLabel(selectedOption))
        } else {
          setInputValue(String(searchParam))
        }
      } else if (homeIdParam && !isNaN(Number(homeIdParam))) {
        const selectedOption = homeOptions.find(option => option.value === homeIdParam)
        if (selectedOption) {
          setInputValue(getDisplayLabel(selectedOption))
        } else {
          setInputValue(String(homeIdParam))
        }
      } else {
        setInputValue(String(searchParam))
      }
    }
  }, [selectedValue, currentParams.search, currentParams.homeId, homeOptions, formMode, updateUrl, getDisplayLabel])

  // Handle input change
  const handleInputChange = (value: string) => {
    setInputValue(value)
    // Open dropdown when user starts typing
    if (value.trim() && !isDropdownOpen) {
      setIsDropdownOpen(true)
    }
  }

  // Handle input focus - open dropdown
  const handleInputFocus = () => {
    setIsDropdownOpen(true)
  }

  // Handle input blur
  const handleInputBlur = () => {
    // Don't close dropdown on blur - let click outside handler manage it
  }

  // Handle input click - open dropdown
  const handleInputClick = () => {
    setIsDropdownOpen(true)
  }

  // Handle option selection from dropdown
  const handleOptionSelect = (option: HomeOption) => {
    setInputValue(getDisplayLabel(option))
    setIsDropdownOpen(false)
    
    // Call the value change callback
    onValueChange?.(option.value)
    
    if (updateUrl) {
      // Update URL with selected home ID (for map page)
      const updatedParams = {
        ...currentParams,
        search: option.value,
        homeId: option.value,
        tab: "map"
      }
      
      const url = buildUrl(pathname, updatedParams)
      router.push(url)
    }
    
    // Call the onSelectHouse callback if provided
    onSelectHouse?.(option.value)
  }

  // Handle search (Enter key)
  const handleSearch = () => {
    if (!inputValue.trim()) return
    
    setIsDropdownOpen(false)
    
    if (updateUrl) {
      // Update URL with search text (for map page)
      const updatedParams = {
        ...currentParams,
        search: inputValue.trim(),
        tab: "map"
      }
      
      const url = buildUrl(pathname, updatedParams)
      router.push(url)
    }
  }

  // Handle clear
  const handleClear = () => {
    setInputValue("")
    setIsDropdownOpen(false)
    
    // Call the value change callback
    onValueChange?.("")
    
    if (updateUrl) {
      // Clear search from URL (for map page)
      const updatedParams = {
        ...currentParams,
        search: undefined,
        homeId: undefined,
        tab: "map"
      }
      
      const url = buildUrl(pathname, updatedParams)
      router.push(url)
    }
    
    // Call the onSelectHouse callback with null
    onSelectHouse?.(null)
  }

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false)
      inputRef.current?.blur()
    }
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return
      
      const target = event.target as Node
      const container = containerRef.current
      
      // Check if the click is outside the container
      if (!container.contains(target)) {
        // Additional check: if clicking on scrollbar or within popover content, don't close
        const targetElement = target as Element
        
        // Check if clicking on scrollbar elements
        if (targetElement && (
          targetElement.classList.contains('scrollbar-thumb') ||
          targetElement.classList.contains('scrollbar-track') ||
          targetElement.closest('[data-radix-scroll-area-viewport]') ||
          targetElement.closest('[data-radix-popper-content-wrapper]')
        )) {
          return // Don't close if clicking on scrollbar or popover content
        }
        
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  // Handle Popover open change
  const handlePopoverOpenChange = (open: boolean) => {
    setIsDropdownOpen(open)
  }

  // Show selected card if enabled and home is selected
  if (showSelectedCard && selectedHome) {
    return (
      <div className={cn("w-full", className)}>
        <div className="flex items-center justify-between p-3 border rounded bg-background">
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              {selectedHome.homeowner?.user?.fullname
                ? (shouldMaskHomeownerData(session?.role, session?.sub, selectedHome.homeowner?.user?.id)
                    ? maskHomeownerLastName(selectedHome.homeowner.user.fullname)
                    : selectedHome.homeowner.user.fullname)
                : 'Unknown'}
            </p>
            <p className="text-sm text-muted-foreground">
              {shouldMaskHomeownerData(session?.role, session?.sub, selectedHome.homeowner?.user?.id)
                ? maskHomeownerAddress(selectedHome.address)
                : selectedHome.address}
            </p>
            {selectedHome.homeowner?.user?.phone && (
              <p className="text-xs text-muted-foreground mt-1">
                {shouldMaskHomeownerData(session?.role, session?.sub, selectedHome.homeowner?.user?.id)
                  ? maskHomeownerPhone(selectedHome.homeowner.user.phone)
                  : selectedHome.homeowner.user.phone}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <Popover 
        open={isDropdownOpen} 
        onOpenChange={handlePopoverOpenChange}
      >
        {/* Use PopoverAnchor instead of PopoverTrigger for the input area
            This prevents Radix from automatically toggling the popover on click */}
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => handleInputChange((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onClick={handleInputClick}
              placeholder={placeholder}
              disabled={disabled || isLoadingHomes}
              className="h-14 pr-20"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {inputValue && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClear()
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                  className="hover:bg-accent rounded p-1 transition-colors"
                  tabIndex={-1}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsDropdownOpen(!isDropdownOpen)
                }}
                onMouseDown={(e) => {
                  // Prevent event from bubbling to click outside handler
                  e.stopPropagation()
                }}
                className="hover:bg-accent rounded p-1 transition-colors"
                tabIndex={-1}
              >
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        </PopoverAnchor>
        <PopoverContent 
          className="p-0 w-[var(--radix-popover-trigger-width)]" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-1">
              {isLoadingHomes ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading homes...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {inputValue.trim() ? "No homes found matching your search." : emptyText}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleOptionSelect(option)}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    <span className="truncate">{getDisplayLabel(option)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default ReusableHomeSearchInput